// apps/client/src/components/layout/AddOnSubappLayout.tsx
// Layout wrapper for the Lead Capture paid add-on subapp.
//
// Responsibilities:
//   1. Enforce subscription gating via FeatureGate — non-subscribers see
//      the existing UpgradePromptCard paywall instead of any subapp content.
//   2. Render the contextual top nav (page tabs) for the subapp.
//   3. Outlet for the routed child page.
//
// Keeps subapp-specific behaviour (badges, header chrome) encapsulated so
// individual pages don't re-render nav or re-check entitlements.
//
// NOTE: Broker Activity (Pipeline / Scorecard / Weekly Funnel) no longer uses
// this layout — those pages render under the MAIN app shell (AppLayout +
// UnifiedTopBar) with a per-page FeatureGate. Lead Capture remains the sole
// consumer of this contextual-subapp shell.

import React from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import FeatureGate from '../FeatureGate';
import { Feature } from '../../config/subscriptionTiers';
import { LeadCaptureTopNav } from './LeadCaptureTopNav';
import { isEdmEnabled } from '../../utils/edm';
import { isGrowthProposeEnabled } from '../../config/leadTabAccess';

interface ParentOutletContext {
  companyData?: { featureFlags?: Record<string, unknown> | null } | null;
}

type AddOn = 'lead_capture';

interface AddOnSubappLayoutProps {
  addOn: AddOn;
  // Optional runtime badge values sourced by the parent route (Firestore
  // real-time hook would wire these in later waves). Omitted = no badge.
  inboxBadge?: number;
  setupStepsComplete?: number;
}

export const AddOnSubappLayout: React.FC<AddOnSubappLayoutProps> = ({
  addOn,
  inboxBadge,
  setupStepsComplete,
}) => {
  // React Router's <Outlet /> does NOT forward the parent route's context
  // by default — nested children see `undefined`. We pull the AppLayout's
  // context here and pass it through explicitly so every subapp page can
  // still call useOutletContext<{ userData?: ... }>() and get back the
  // companyId / role it needs.
  const parentCtx = useOutletContext<unknown>();
  // The eDM and Growth tabs follow company flags, which only this layout can see.
  const featureFlags = (parentCtx as ParentOutletContext | undefined)?.companyData?.featureFlags;
  const edmEnabled = isEdmEnabled(featureFlags);
  const growthProposeEnabled = isGrowthProposeEnabled(featureFlags);

  return (
    <FeatureGate feature={addOn as Feature}>
      <div className="flex flex-col h-full">
        <LeadCaptureTopNav
          inboxBadge={inboxBadge}
          setupStepsComplete={setupStepsComplete}
          edmEnabled={edmEnabled}
          growthProposeEnabled={growthProposeEnabled}
        />
        {/* This is the subapp's real scroller, so it must be the containing
            block (`relative`): otherwise an absolutely positioned descendant
            (an sr-only label far down a long page) anchors to AppLayout's
            <main>, makes <main> scroll as well, and the page is cut off above
            an empty band at the bottom of the viewport. */}
        <div className="relative flex-1 min-h-0 overflow-y-auto">
          <Outlet context={parentCtx} />
        </div>
      </div>
    </FeatureGate>
  );
};

export default AddOnSubappLayout;
